/**
 * Q&A Bot Insights Section for Accountant Report View
 * Displays common forensic accountant review questions with AI-powered answers
 */

import React from 'react';

interface QAInsightsSectionProps {
  result: any;
  report: any;
  calcDetail: any;
  formatCurrency: (amount?: number) => string;
  formatPercent: (value?: number) => string;
}

export default function QAInsightsSection({
  result,
  report,
  calcDetail,
  formatCurrency,
  formatPercent
}: QAInsightsSectionProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
        <span className="text-2xl mr-2">💬</span>
        Key Review Questions & Methodology Insights
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Common questions forensic accountants ask during review, with AI-powered answers based on the calculation methodology and supporting documentation.
      </p>

      <div className="space-y-4">
        {/* Question 1 */}
        <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-blue-600 font-semibold">Q</span>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900 mb-2">How was the benchmark period selected?</p>
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-green-700">Answer:</span> The benchmark period ({calcDetail?.benchmark_period?.start} to {calcDetail?.benchmark_period?.end}) 
                was selected using BI methodology to maximize the VP difference and represent comparable seasonal conditions. 
                This {calcDetail?.benchmark_period?.type || 'period'} represents normal operations before the interruption event.
              </p>
            </div>
          </div>
        </div>

        {/* Question 2 */}
        <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-blue-600 font-semibold">Q</span>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900 mb-2">Why is the Claimant Factor negative?</p>
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-green-700">Answer:</span> The Claimant Factor of {formatPercent(calcDetail?.growth_adjustment?.claimant_factor_pct)} 
                indicates the business was experiencing a decline before the interruption. This is calculated using regression analysis of 3-4 years of 
                historical revenue data. The negative adjustment ensures we only claim losses caused by the event, not pre-existing business decline.
              </p>
            </div>
          </div>
        </div>

        {/* Question 3 */}
        <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-blue-600 font-semibold">Q</span>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900 mb-2">Is the Variable Margin reasonable for this business type?</p>
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-green-700">Answer:</span> The Variable Margin of {formatPercent(calcDetail?.variable_profit_analysis?.variable_margin_pct)} 
                is {calcDetail?.variable_profit_analysis?.variable_margin_pct > 85 ? 'typical for nonprofits and churches' : 'within normal range'}, 
                where most costs are fixed (building, salaries, utilities) and variable costs are minimal. 
                This high margin is validated by the P&L analysis showing {formatCurrency(calcDetail?.benchmark_period?.revenue - calcDetail?.benchmark_period?.variable_profit)} 
                in variable expenses against {formatCurrency(calcDetail?.benchmark_period?.revenue)} in revenue.
              </p>
            </div>
          </div>
        </div>

        {/* Question 4 */}
        <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-blue-600 font-semibold">Q</span>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900 mb-2">Has causation been tested?</p>
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-green-700">Answer:</span> Yes, causation {result?.causation_passed ? 'PASSED' : 'FAILED'} 
                using {result?.causation_test_used || 'standard causation testing'}. 
                {result?.causation_passed 
                  ? ' The interruption event is proven to be the direct cause of the revenue loss, meeting the policy requirements for coverage.' 
                  : ' The claim did not meet causation requirements and may not be covered under the policy.'}
              </p>
            </div>
          </div>
        </div>

        {/* Question 5 */}
        <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-blue-600 font-semibold">Q</span>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900 mb-2">What documentation supports this claim?</p>
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-green-700">Answer:</span> The claim is supported by: 
                (1) P&L statements from benchmark and coverage periods showing actual revenue and expenses,
                (2) Payroll reconciliation with {formatPercent(result?.payroll_reconciliation?.variance_pct || 0)} variance,
                (3) Historical revenue data spanning {report.year_window?.[0]} to {report.year_window?.[report.year_window.length - 1]},
                (4) Event documentation showing interruption dates and impact.
                All source documents are available for audit.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-semibold text-blue-900 mb-1">💡 For Forensic Review</p>
            <p className="text-sm text-blue-800">
              These are the most common questions asked during forensic accountant review. 
              Each answer is derived from the actual calculation methodology and source documents. 
              Additional questions can be answered by reviewing the detailed methodology sections above.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
